import type { BetterFetch } from "@better-fetch/fetch";
import type { Store } from "@di-devs/better-feature/types";
import { atom } from "nanostores";
import { memberPointEndpointContract } from "../../shared/contracts/memberPoint.contracts";
import type { PointWithRelations } from "../../shared/types/memberPoint.types";
import type {
	BulkUpdateMemberPointsRequest,
	GetDailyPointSummaryQueryParams,
	GetMemberPointHistoryQueryParams,
	UpdateMemberPointsRequest,
} from "../../shared/types/memberPointApi.types";
import {
	createQuerySetMap,
	extractListeners,
	extractQueries,
	extractSignals,
} from "../shared/queryFactory";

export function createMemberPointPlugin($fetch: BetterFetch, $store?: Store) {
	const querySets = createQuerySetMap(memberPointEndpointContract, $fetch);

	return {
		signals: extractSignals(querySets),
		queries: extractQueries(querySets),
		listeners: extractListeners(querySets),
		atoms: {
			$pointHistory: atom<PointWithRelations[] | null>(null),
			$currentBalance: atom<number>(0),
			$isPointsModalVisible: atom<boolean>(false),
			$pointsLoading: atom<boolean>(false),
			$pointsError: atom<string | null>(null),
		},
		actions: {
			async updateMemberPoints(
				memberId: string,
				data: UpdateMemberPointsRequest,
			) {
				const result = await $fetch<
					typeof memberPointEndpointContract.updateMemberPoints.responseType
				>(
					memberPointEndpointContract.updateMemberPoints.path.replace(
						":memberId",
						memberId,
					),
					{
						method: memberPointEndpointContract.updateMemberPoints.method,
						body: data,
					},
				);
				const currentBalance = $store?.atoms?.$currentBalance.get();
				const multiplier = data.isAdd ? 1 : -1;
				const pointsChange = data.pointAmount * multiplier || 0;
				$store?.atoms?.$currentBalance.set(currentBalance + pointsChange);
				return result;
			},
			async getMemberPointHistory(
				memberId: string,
				params?: GetMemberPointHistoryQueryParams,
			) {
				const pointHistory = await $fetch<
					typeof memberPointEndpointContract.getMemberPointHistory.responseType
				>(
					memberPointEndpointContract.getMemberPointHistory.path.replace(
						":memberId",
						memberId,
					),
					{
						method: memberPointEndpointContract.getMemberPointHistory.method,
						query: params,
					},
				);
				$store?.atoms?.$pointHistory.set(pointHistory);
				return pointHistory;
			},
			async getDailyPointSummary(
				memberId: string,
				params?: GetDailyPointSummaryQueryParams,
			) {
				const dailySummary = await $fetch<
					typeof memberPointEndpointContract.getDailyPointSummary.responseType
				>(
					memberPointEndpointContract.getDailyPointSummary.path.replace(
						":memberId",
						memberId,
					),
					{
						method: memberPointEndpointContract.getDailyPointSummary.method,
						query: params,
					},
				);
				return dailySummary;
			},
			async bulkUpdateMemberPoints(data: BulkUpdateMemberPointsRequest) {
				const result = await $fetch<
					typeof memberPointEndpointContract.bulkUpdateMemberPoints.responseType
				>(memberPointEndpointContract.bulkUpdateMemberPoints.path, {
					method: memberPointEndpointContract.bulkUpdateMemberPoints.method,
					body: data,
				});
				return result;
			},
		},
		querySets, // Expose the raw querySets with proper typing
	};
}
